import React from "react";
import { Search } from "lucide-react";
import ChatItem, { getDisplayName } from "./ChatItem";
import defaultAvatar from "../assets/profile.png";

const ChatList = ({
  chats,
  search,
  onSearchChange,
  friendResults,
  searchingFriends,
  selectedChatId,
  onlineIds,
  currentUserId,
  loading,
  error,
  hasMore,
  loadingMore,
  onLoadMore,
  onRetry,
  onSelectChat,
  onStartChat,
  onCreateChat,
}) => {
  const term = search.trim().toLowerCase();
  const visibleChats = term
    ? chats.filter((chat) =>
        `${chat.name} ${chat.username || ""}`.toLowerCase().includes(term)
      )
    : chats;

  return (
    <div className="w-full">
      {/* Search Friend */}
      <div className="relative w-full mb-4">
        <input
          type="text"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Find Your Friend"
          className="w-full pl-10 pr-4 py-3 text-sm text-[#000000] placeholder-[#B8B8B8] border-none rounded-xl outline-none focus:ring-2 focus:ring-green-500 bg-[#F0F0F0]"
        />
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#B8B8B8]" />
      </div>

      <div className="pt-2 pr-2 md:max-h-[calc(70vh-48px)] md:min-h-[400px] md:overflow-y-auto">
        {loading && <p className="text-sm text-gray-500 text-center py-6">Loading chats...</p>}

        {!loading && error && (
          <div className="text-center py-6">
            <p className="text-sm text-red-500 mb-2">{error}</p>
            <button onClick={onRetry} className="text-sm font-semibold text-[#095228] underline">
              Try again
            </button>
          </div>
        )}

        {!loading && !error && visibleChats.length === 0 && !term && (
          <div className="text-center py-6">
            <p className="text-sm text-gray-500 mb-2">No conversations yet.</p>
            <button onClick={onCreateChat} className="text-sm font-semibold text-[#095228] underline">
              Start a new chat
            </button>
          </div>
        )}

        {visibleChats.map((chat) => (
          <ChatItem
            key={chat.id}
            chat={chat}
            isActive={Number(chat.id) === Number(selectedChatId)}
            isOnline={onlineIds.has(String(chat.id))}
            currentUserId={currentUserId}
            onClick={() => onSelectChat(chat)}
          />
        ))}

        {!loading && !term && hasMore && (
          <button
            onClick={onLoadMore}
            disabled={loadingMore}
            className="w-full text-sm font-semibold text-[#095228] py-2 disabled:opacity-50"
          >
            {loadingMore ? "Loading..." : "Load more"}
          </button>
        )}

        {term && (
          <div className="mt-2">
            <h4 className="text-xs font-semibold text-[#797C7B] uppercase mb-2">Friends</h4>
            {searchingFriends && <p className="text-sm text-gray-500">Searching...</p>}
            {!searchingFriends && friendResults.length === 0 && (
              <p className="text-sm text-gray-500">
                {visibleChats.length ? "No other friends match your search." : "No friends match your search."}
              </p>
            )}
            {friendResults.map((friend) => (
              <button
                type="button"
                key={friend.id}
                onClick={() => onStartChat(friend)}
                className="w-full flex items-center gap-3 p-2 rounded-lg text-left hover:bg-gray-100"
              >
                <img
                  src={friend.profile_picture_url || defaultAvatar}
                  alt={getDisplayName(friend)}
                  className="w-10 h-10 rounded-lg border object-cover"
                />
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-[#000000] truncate">{getDisplayName(friend)}</p>
                  {friend.username && <p className="text-xs text-[#797C7B] truncate">@{friend.username}</p>}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatList;
